import { axiosInstance } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";
import type { TokenizeCardErrors } from "@hopper-b2b/types";
import type { AxiosError } from "axios";
import type {
  SpreedlyCreatePaymentMethodErrorResponse,
  SpreedlyCreatePaymentMethodRequest,
  SpreedlyCreatePaymentMethodResponse,
  SpreedlyCreatePaymentMethodTransaction,
} from "../types";
import { isSpreedlyCreatePaymentMethodError } from "../utils";

const SPREEDLY_CORE_URL = "https://core.spreedly.com";
const SPREEDLY_PAYMENT_METHOD_URI = "/v1/payment_methods.json";

export type PostSpreedlyPaymentMethodArgs = {
  key: string;
  data: SpreedlyCreatePaymentMethodRequest;
  onSuccess: (token: string, last4: string) => void;
  onError: (error) => void;
  onFieldErrors: (errors: TokenizeCardErrors[]) => void;
};

export const postSpreedlyPaymentMethod = async ({
  key,
  data,
  onSuccess,
  onError,
  onFieldErrors,
}: PostSpreedlyPaymentMethodArgs): Promise<
  Pick<SpreedlyCreatePaymentMethodTransaction, "succeeded">
> => {
  try {
    const res = await axiosInstance.post<SpreedlyCreatePaymentMethodResponse>(
      `${SPREEDLY_CORE_URL}${SPREEDLY_PAYMENT_METHOD_URI}?environment_key=${key}`,
      data
    );

    const { payment_method: paymentMethod, succeeded } =
      res?.data?.transaction ?? {};
    onSuccess(paymentMethod.token, paymentMethod.last_four_digits);

    return {
      succeeded,
    };
  } catch (e: AxiosError<SpreedlyCreatePaymentMethodErrorResponse> | any) {
    if (
      isSpreedlyCreatePaymentMethodError(e) &&
      e.response.data.errors.length > 0
    ) {
      const fieldErrors = e.response.data.errors.map(
        ({ key, attribute, status }) => ({
          key,
          attribute,
          status,
        })
      );
      console.error("Spreedly card tokenization field error", fieldErrors);
      onFieldErrors(e.response.data.errors);
    } else {
      const errorDetails = {
        message: e?.message,
        code: e?.code,
        status: e?.response?.status,
        statusText: e?.response?.statusText,
      };
      console.error("Spreedly card tokenization error", errorDetails);
      onError(e);
    }

    return {
      succeeded: false,
    };
  }
};
